import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { BehaviorSubject, Observable, tap } from 'rxjs'
import { environment } from '../../../environments/environment'
import { Tracking } from '../../models/tracking.interface'
import { Pagination } from '../../models/pagination.interface'
import { QueryGet } from '../../shared/query-types'
import { RequestService } from '../request/request.service'
import { AsyncFormValidation } from '../../interceptors/error.interceptor'
import { Accompaniment } from '../../models/accompaniment.interface'

@Injectable({
    providedIn: 'root',
})
export class TrackingService {
    private apiUrl = environment.service_1 + 'create-tracking'
    private url = environment.service_1 + 'update-tracking/'
    private apiUrlList = environment.service_1 + 'trackings/'
    private urlDelete = environment.service_1 + 'delete-tracking/'

    private trackingsSubject = new BehaviorSubject<Tracking[]>([])
    public trackings$ = this.trackingsSubject.asObservable()

    private selectedTrackingSubject = new BehaviorSubject<Tracking | null>(null)
    public selectedTrackingSubject$ =
        this.selectedTrackingSubject.asObservable()

    private asyncErrorValidation =
        new BehaviorSubject<AsyncFormValidation | null>(null)
    asyncErrorValidation$ = this.asyncErrorValidation.asObservable()


    constructor(
        private http: HttpClient,
        private requestService: RequestService
    ) {}

    setTrackings(trackings: Tracking[]): void {
        this.trackingsSubject.next(trackings)
    }

    getAll(): Observable<Tracking[]> {
        return this.http.get<Tracking[]>(this.apiUrl)
    }

    createTracking(payload: Tracking | FormData): Observable<Tracking> {
        return this.http.post<Tracking>(this.apiUrl, payload).pipe(
            tap({
                error: (err: HttpErrorResponse) => {
                    this.asyncErrorValidation.next(err.error)
                },
            })
        )
    }

    allTrackings(queryParams: QueryGet): Observable<Pagination<Tracking[]>> {
        return this.requestService.get<Tracking[]>(this.apiUrlList, queryParams)
    }

    getTrackingById(id: number): Observable<Tracking> {
        return this.http.get<Tracking>(`${this.apiUrl}${id}/`)
    }

    updateTracking(
        id: number,
        payload: Tracking | FormData
    ): Observable<Tracking> {
        return this.http.put<Tracking>(`${this.url}${id}`, payload).pipe(
            tap({
                error: (err: HttpErrorResponse) =>
                    this.asyncErrorValidation.next(err.error),
            })
        )
    }

    deleteTracking(id: number): Observable<any> {
        return this.http.delete(`${this.urlDelete}${id}`)
    }

    setSelectedTracking(tracking: Tracking): void {
        this.selectedTrackingSubject.next(tracking)
    }

    removeSelectedTracking(): void {
        this.selectedTrackingSubject.next(null)
    }

    getSelectedTracking(): Tracking | null {
        return this.selectedTrackingSubject.value
    }

    calculateAge(dateBirth: Date | string | null | undefined): number | null {
        if (!dateBirth) return null

        const birth =
            typeof dateBirth === 'string'
                ? this.toLocalDateOnly(dateBirth)
                : new Date(dateBirth)

        if (!birth || isNaN(birth.getTime())) return null

        const today = new Date()
        let age = today.getFullYear() - birth.getFullYear()
        const month = today.getMonth() - birth.getMonth()

        if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
            age--
        }
        return age
    }

    toLocalDateOnly(value: string | Date | null): Date | null {
        if (!value) return null

        if (typeof value === 'string') {
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
            if (m) return new Date(+m[1], +m[2] - 1, +m[3])
            const d = new Date(value)
            return new Date(d.getFullYear(), d.getMonth(), d.getDate())
        }
        return new Date(value.getFullYear(), value.getMonth(), value.getDate())
    }

    toYyyyMmDd(date: Date | null): string | null {
        if (!date) return null
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
    }

}
